// Stage 1a — AE Details review page (FR-1.11–1.17 / US-1a.1–1a.8). Opened from a preprint card's
// "Details" action. Renders the pitch deck in two clearly-labelled halves: the student-authored
// CLAIM half (AE sanity-checks) and the reviewer-profile half the AE is responsible for verifying.
// Partial edits save without clobbering unedited fields; refine + status changes update live (SSE).
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type SelectedStatus,
  type SelectedView,
} from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { useAuth } from "../../lib/auth.js";
import { useCan } from "../../components/Can.js";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Spinner,
  StarRating,
  Textarea,
  statusTone,
} from "../../components/ui.js";
import { ProposeReviewer } from "./ProposeReviewer.js";
import { confirmAdvance } from "./advance-confirms.js";

// Deck field descriptors — the `key` is the verbatim `Selected.pitch` key (the field contract).
type FieldKind = "textarea" | "text" | "stars";
interface DeckField {
  key: string;
  kind: FieldKind;
}
const CLAIM_FIELDS: DeckField[] = [
  { key: "Research Q1", kind: "textarea" },
  { key: "Main Claim 1", kind: "textarea" },
  { key: "Claim 1 Rating", kind: "stars" },
  { key: "Claim 1 Rating Confidence", kind: "stars" },
  { key: "Research Q2", kind: "textarea" },
  { key: "Main Claim 2", kind: "textarea" },
  { key: "Claim 2 Rating", kind: "stars" },
  { key: "Claim 2 Rating Confidence", kind: "stars" },
  { key: "Research Q3", kind: "textarea" },
  { key: "Main Claim 3", kind: "textarea" },
  { key: "Claim 3 Rating", kind: "stars" },
  { key: "Claim 3 Rating Confidence", kind: "stars" },
  { key: "Strength of Evidence", kind: "stars" },
  { key: "Potential Limitation", kind: "textarea" },
  { key: "Method Used 1", kind: "text" },
  { key: "Method Used 2", kind: "text" },
  { key: "Method Used 3", kind: "text" },
  { key: "Reason(value-add)", kind: "textarea" },
  { key: "Reason(impact/importance)", kind: "textarea" },
  { key: "Reason(urgency)", kind: "textarea" },
];
const PROFILE_TEXT_FIELDS: DeckField[] = [
  { key: "Reviewer Profile(discipline)", kind: "text" },
  { key: "Reviewer Profile(method expertise)", kind: "textarea" },
  { key: "Reviewer Profile(topical expertise)", kind: "text" },
];
// Editable in edit mode as raw "Name — link" strings (they were only editable in the retired
// Pitch Deck popup — FR-K3 consolidated all deck editing here); presentation mode renders the
// parsed <ModelReviewers> cards instead.
const MODEL_REVIEWER_FIELDS: DeckField[] = [
  { key: "Peer Reviewer Model #1", kind: "text" },
  { key: "Peer Reviewer Model #2", kind: "text" },
];

const REFINE_TONE: Record<string, "slate" | "blue" | "green" | "red"> = {
  None: "slate",
  Created: "blue",
  Refined: "green",
};
const refineTone = (s: string | null): "slate" | "blue" | "green" | "red" =>
  s == null ? "slate" : (REFINE_TONE[s] ?? (s.startsWith("Failed") ? "red" : "slate"));

export function PreprintDetails(): JSX.Element {
  const { id = "" } = useParams();
  const { data: s, isLoading, isError } = useQuery({
    queryKey: qk.selectedOne(id),
    queryFn: () => api.get<SelectedView>(`/preprints/selected/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <Spinner label="Loading preprint…" />;
  if (isError || !s) return <EmptyState>Preprint not found. <Link className="text-brand hover:underline" to="/preprints">Back to list</Link></EmptyState>;
  return <DetailsView s={s} />;
}

function DetailsView({ s }: { s: SelectedView }): JSX.Element {
  const qc = useQueryClient();
  const can = useCan();
  const { user } = useAuth();
  const own = !!user && !!s.assigneeEmail && s.assigneeEmail.toLowerCase() === user.email.toLowerCase();
  const ctx = { domain: s.domain };
  // July-8 FR-N1: an AE edits BOTH halves of ANY preprint regardless of domain. `advanceToPitch
  // Editorial` with no domain scope is exactly the AE-and-above test (students and QC-in-training
  // are denied it outright), so it doubles as the "am I an AE?" tell — see FR-L8's cross-domain
  // advance. The server re-checks this on every write (deckScope in routes/preprints.ts).
  const isAe = can("advanceToPitchEditorial", {});
  const canEdit = own || isAe || can("editPitchDeck", ctx);
  const canProfile = isAe || can("editSimilarArticles", ctx); // profile half + refine

  // Presentation mode (FR-J1): a pitched deck opens read-only (whole-screen, screen-share
  // friendly, split-screen per FR-J7); editing is a separate mode entered deliberately.
  const pitched = s.status !== "Unread" && s.status !== "Taken";
  const [editMode, setEditMode] = useState(!pitched);

  // Draft = only the fields the AE has touched, so Save sends a minimal partial merge (FR-1.16).
  const [draft, setDraft] = useState<Record<string, string>>({});
  const dirtyKeys = Object.keys(draft);
  const dirty = dirtyKeys.length > 0;
  const valueOf = (key: string): string =>
    key in draft ? draft[key]! : String((s.pitch as Record<string, unknown>)[key] ?? "");
  const setField = (key: string, value: string): void => setDraft((d) => ({ ...d, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const pitch: Record<string, string> = {};
      for (const k of dirtyKeys) pitch[k] = draft[k] ?? "";
      return api.patch<SelectedView>(`/preprints/selected/${s.id}`, { pitch });
    },
    onSuccess: (updated) => {
      qc.setQueryData(qk.selectedOne(s.id), updated);
      qc.invalidateQueries({ queryKey: ["selected"] });
      setDraft({});
    },
  });

  const isUrgent = valueOf("Urgent") === "Urgent";
  const setUrgent = useMutation({
    mutationFn: (urgent: boolean) => api.post(`/preprints/selected/${s.id}/urgent`, { urgent }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["selected"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Link className="text-brand hover:underline" to="/preprints">← Preprints</Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(s.status)}>{s.status}</Badge>
            {s.domain && <Badge tone="blue">{s.domain}</Badge>}
            {s.gatesFunded && <Badge tone="amber">Gates</Badge>}
            {isUrgent && <Badge tone="red">Urgent</Badge>}
            <span className="text-xs text-slate-500">
              Refinement: <Badge tone={refineTone(s.proposalStatus)}>{s.proposalStatus ?? "None"}</Badge>
            </span>
          </div>
          <h1 className="mt-1 text-xl font-bold">{s.title}</h1>
          <p className="text-xs text-slate-500">
            {s.assigneeEmail ? `Assigned: ${s.assigneeEmail}` : "Unassigned"}
            {s.doi && (
              <>
                {" · "}
                <a className="text-brand hover:underline" href={s.doi} target="_blank" rel="noreferrer">Open DOI</a>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(canEdit || canProfile) && (
            <Button variant="secondary" onClick={() => setEditMode((m) => !m)} aria-pressed={editMode}>
              {editMode ? "Presentation view" : "Edit deck"}
            </Button>
          )}
          {editMode && canEdit && (
            <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
          )}
        </div>
      </header>

      {/* Split-screen (FR-J7): student half (claims) left, AE half (reviewer profile) right. */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Student half — claims ──────────────────────────────────── */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Student half — claims</h2>
              <p className="text-xs text-slate-500">Student’s summary of the science — AE sanity-checks.</p>
            </div>
            {editMode && (
              <Button
                variant="ghost"
                onClick={() => setUrgent.mutate(!isUrgent)}
                disabled={!canEdit || setUrgent.isPending}
                aria-pressed={isUrgent}
              >
                {isUrgent ? "Unset urgent" : "Set urgent"}
              </Button>
            )}
          </CardHeader>
          <CardBody className="space-y-3">
            {CLAIM_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canEdit}
                present={!editMode}
              />
            ))}
          </CardBody>
        </Card>

        {/* ── AE half — reviewer profile ─────────────────────────────── */}
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">AE half — reviewer profile</h2>
            <p className="text-xs text-slate-500">
              Drives the Stage 2 reviewer search — the student drafts it, the AE verifies and finalizes.
            </p>
          </CardHeader>
          <CardBody className="space-y-4">
            {PROFILE_TEXT_FIELDS.map((f) => (
              <DeckFieldInput
                key={f.key}
                field={f}
                value={valueOf(f.key)}
                onChange={(v) => setField(f.key, v)}
                readOnly={!canProfile && !canEdit}
                present={!editMode}
              />
            ))}

            {/* Edit mode exposes the raw model-reviewer strings; presentation shows parsed cards.
                The "Most Similar Articles" (Prophy) and "Related Works" (OpenAlex) dropdowns are
                gone (FR-K7 — "it's not live"); the fields stay in the model, just no UI. */}
            {editMode ? (
              MODEL_REVIEWER_FIELDS.map((f) => (
                <DeckFieldInput
                  key={f.key}
                  field={f}
                  value={valueOf(f.key)}
                  onChange={(v) => setField(f.key, v)}
                  readOnly={!canProfile && !canEdit}
                />
              ))
            ) : (
              <ModelReviewers reviewers={s.modelReviewers} />
            )}

            {/* FR-K6: Nearest Neighbors inline, expanded — "they play a big part". */}
            <NearestNeighborPapers raw={String((s.pitch as Record<string, unknown>)["Nearest Neighbor Papers"] ?? "")} />
          </CardBody>
        </Card>
      </div>

      {/* ── Refine + Finalize gate, then the status controls (FR-N2/N3) ─ */}
      <ChangeStatus s={s} own={own} canProfile={canProfile} />

      {/* ── Embedded Propose a Reviewer (COI-first; hidden in presentation mode) ─ */}
      {editMode && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold">Propose a Reviewer</h2>
            <p className="text-xs text-slate-500">Runs the COI checks first; a passing reviewer is complete on submit.</p>
          </CardHeader>
          <CardBody>
            <ProposeReviewer selectedId={s.id} canPropose={own || can("claimEvaluateSuggestion", ctx)} />
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function DeckFieldInput({
  field,
  value,
  onChange,
  readOnly,
  present = false,
}: {
  field: DeckField;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  /** Presentation mode (FR-J1): clean read-only text, no edit chrome. */
  present?: boolean;
}): JSX.Element {
  if (present) {
    return (
      <div>
        <span className="text-xs font-medium text-slate-600">{field.key}</span>
        {field.kind === "stars" ? (
          <StarRating value={Number.parseInt(value, 10) || 0} readOnly label={field.key} />
        ) : (
          <p className="whitespace-pre-wrap text-sm text-slate-800">{value || <span className="text-slate-400">—</span>}</p>
        )}
      </div>
    );
  }
  return (
    <Field label={field.key}>
      {field.kind === "stars" ? (
        <StarRating value={Number.parseInt(value, 10) || 0} onChange={(n) => onChange(String(n))} readOnly={readOnly} label={field.key} />
      ) : field.kind === "textarea" ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
      )}
    </Field>
  );
}

function ModelReviewers({ reviewers }: { reviewers: SelectedView["modelReviewers"] }): JSX.Element {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-600">Model Peer Reviewers</h3>
      {reviewers.length === 0 ? (
        <p className="text-xs text-slate-400">None provided.</p>
      ) : (
        <ul className="mt-1 space-y-2">
          {reviewers.map((r) => (
            <li key={r.slot} className="rounded-md border border-slate-200 p-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{r.name || `Model #${r.slot}`}</span>
                <Badge tone="slate">#{r.slot}</Badge>
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs">
                {r.profileUrl && (
                  <a className="text-brand hover:underline" href={r.profileUrl} target="_blank" rel="noreferrer">Profile ↗</a>
                )}
                {r.pubmedUrl && (
                  <a className="text-brand hover:underline" href={r.pubmedUrl} target="_blank" rel="noreferrer">PubMed ↗</a>
                )}
                {!r.profileUrl && !r.pubmedUrl && <span className="text-slate-400">No links parsed</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Inline + expanded by default (FR-K6): the team reads through these during pitches. */
function NearestNeighborPapers({ raw }: { raw: string }): JSX.Element | null {
  if (!raw.trim()) return null;
  return (
    <div className="text-xs">
      <h3 className="font-semibold text-slate-600">Nearest Neighbor Papers</h3>
      <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-slate-50 p-2 text-slate-600">{raw}</pre>
    </div>
  );
}

// Change-Status gate — advance / select / reject, gated by can(...) and the same action mapping
// the API enforces. Selection is direct + AE-gated (July-2 FR-J4).
//
// July-8 FR-N2/FR-N3 turn the two AE gates into a visible, auditable checklist (the `ae-pitch-deck`
// mockup's "gate" bar): Refine Concepts must have run, and the AE must tick "I have reviewed &
// finalized the deck" — only then does Advance light up. The server enforces both (409 otherwise);
// disabling the button is a courtesy, not the control. Setting `Selected` still fires the "changes
// discussed in editorial?" confirm (FR-J5 semantics, retained by FR-N2).
const NEXT: Partial<Record<SelectedStatus, { status: SelectedStatus; label: string; action: string }>> = {
  Taken: { status: "To Pitch(Team)", label: "Ready to pitch (Team)", action: "setToPitchTeam" },
  "To Pitch(Team)": { status: "To Pitch(Editorial)", label: "Advance to Editorial", action: "advanceToPitchEditorial" },
  "To Pitch(Editorial)": { status: "Selected", label: "Set Selected", action: "setToSelected" },
};
const REJECT_TARGET: Partial<Record<SelectedStatus, SelectedStatus>> = {
  "To Pitch(Team)": "Team Rejected",
  "To Pitch(Editorial)": "Editorial Rejected",
};
/** The AE gates the FR-N2/N3 checklist guards. The student's own → To Pitch(Team) is not gated. */
const AE_GATES: SelectedStatus[] = ["To Pitch(Team)", "To Pitch(Editorial)"];

const fmtStamp = (iso: string): string => new Date(iso).toLocaleString();

function ChangeStatus({ s, own, canProfile }: { s: SelectedView; own: boolean; canProfile: boolean }): JSX.Element | null {
  const qc = useQueryClient();
  const can = useCan();
  const ctx = { domain: s.domain, beforePitching: s.status === "Taken" };
  const next = NEXT[s.status];
  const rejectTarget = REJECT_TARGET[s.status];
  const [reason, setReason] = useState("");

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ["selected"] });
    qc.invalidateQueries({ queryKey: qk.selectedOne(s.id) });
  };
  const advance = useMutation({
    mutationFn: (status: SelectedStatus) =>
      api.post(`/preprints/selected/${s.id}/advance`, { status, ...(reason ? { reason } : {}) }),
    onSuccess: invalidate,
  });
  const refine = useMutation({
    mutationFn: () => api.post(`/preprints/selected/${s.id}/refine`, { mode: "reviewers" }),
    onSuccess: invalidate,
  });
  const finalize = useMutation({
    mutationFn: (finalized: boolean) => api.post(`/preprints/selected/${s.id}/finalize`, { finalized }),
    onSuccess: invalidate,
  });

  // July-8 FR-L8: AE-gated advances + gate rejects are cross-domain (no domain in the check).
  const canAdvance = !!next && (next.action === "setToPitchTeam" ? own || can("setToPitchTeam", ctx) : can(next.action as never, {}));
  const canReject = !!rejectTarget && can((next?.action ?? "advanceToPitchEditorial") as never, {});
  if (!next && !rejectTarget) return null;

  // ── The FR-N2/N3 gate state ──
  const atAeGate = AE_GATES.includes(s.status);
  const refining = s.proposalStatus === "Created";
  const refined = !!s.refinedAt;
  const finalized = !!s.finalizedAt;
  // FR-N3 blocks both AE gates; FR-N2's Finalize is specifically the door into Editorial.
  const needsFinalize = s.status === "To Pitch(Team)";
  const blockers = atAeGate
    ? [!refined && "Run Refine Concepts first", needsFinalize && !finalized && "Finalize the deck first"].filter(Boolean)
    : [];
  const gateBlocked = blockers.length > 0;

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold">Change Status</h2>
        <p className="text-xs text-slate-500">
          {atAeGate
            ? "Refine and finalize before this deck can advance — both are recorded against your name."
            : "Record your gate decision."}
        </p>
      </CardHeader>

      {/* FR-K8: Refine sits on BOTH pitch gates ("sometimes I get this wrong, so I have to
          re-refine"). FR-N3: it is now a required gate, not just a button. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <Button variant="secondary" onClick={() => refine.mutate()} disabled={refine.isPending || refining}>
            {refining ? "Refining…" : "↻ Refine Concepts / Updated Reviewers"}
          </Button>
          {refined ? (
            <Badge tone="green">✓ Refined {fmtStamp(s.refinedAt!)}</Badge>
          ) : (
            <Badge tone="amber">Required before advancing</Badge>
          )}
        </CardBody>
      )}

      {/* FR-N2: the first-class Finalize control that replaces the pop-up-only gate. */}
      {atAeGate && canProfile && (
        <CardBody className="flex flex-wrap items-center gap-3 border-b border-slate-100">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={finalized}
              disabled={finalize.isPending}
              onChange={(e) => finalize.mutate(e.target.checked)}
            />
            I have reviewed &amp; finalized the deck
          </label>
          {finalized ? (
            <Badge tone="green">
              Finalized by {s.finalizedBy} · {fmtStamp(s.finalizedAt!)}
            </Badge>
          ) : (
            <Badge tone="amber">Required before Editorial</Badge>
          )}
        </CardBody>
      )}

      <CardBody className="flex flex-wrap items-center gap-2">
        {canAdvance && next && (
          <>
            <Button
              onClick={() => {
                // FR-N2 keeps the "changes discussed in editorial?" confirm on → Selected.
                if (!confirmAdvance(next.status)) return;
                advance.mutate(next.status);
              }}
              disabled={advance.isPending || gateBlocked}
              title={gateBlocked ? blockers.join(" · ") : undefined}
            >
              {next.label}
            </Button>
            {gateBlocked && <p className="text-xs text-amber-700">{blockers.join(" · ")}.</p>}
          </>
        )}
        {canReject && rejectTarget && (
          <>
            <Input className="max-w-xs" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm(`Reject this preprint at the ${s.status} gate?`)) advance.mutate(rejectTarget);
              }}
              disabled={advance.isPending}
            >
              Reject
            </Button>
          </>
        )}
        {!canAdvance && !canReject && <p className="text-xs text-slate-500">No status changes available to you here.</p>}
      </CardBody>
    </Card>
  );
}
